/**
 * recentDocuments — a lista "Últimos documentos" da home.
 *
 * ─── POR QUE ISTO NÃO REIMPLEMENTA A FUSÃO ──────────────────────────────────
 *
 * Fundir os tipos numa lista cronológica já existe: `buildClientTimeline`
 * (`src/lib/clientSummary.ts`), que o Resumo do Cliente usa. Reusar não é só
 * economia de código — é o que impede o defeito que já viveu em PRODUÇÃO:
 * **as rotas do app são bilíngues** (`/quotes/:id` em inglês, `/ordens-servico/:id`
 * em português), e `navigate()` para uma rota inventada por analogia compila,
 * não gera warning, não quebra teste nenhum e cai no catch-all mostrando 404
 * (CLAUDE.md #56). A rota é DADO, devolvido pelo agregador, e há teste afirmando
 * as sete. Uma lista de documentos que montasse a própria rota reabriria
 * exatamente essa porta, agora na tela mais vista do app.
 *
 * ─── O QUE ESTE MÓDULO DECIDE: O RECORTE ────────────────────────────────────
 *
 * "Qualquer documento gerado no app" precisa de uma régua, e ela é: **o app
 * gera, o usuário imprime/envia, e existe uma rota para abrir**.
 *
 * - **ENTRAM** orçamento, venda rápida, ordem de serviço, contrato, recibo e
 *   NFS-e. São os seis do ciclo comercial descrito no CLAUDE.md §1.
 * - **FICA DE FORA o compromisso da Agenda.** Ele não é *gerado*, é marcado:
 *   não tem valor, não tem PDF e não tem rota de detalhe — a timeline manda
 *   TODO compromisso para `/agenda`, então numa lista de documentos ele
 *   produziria várias linhas indo para o mesmo lugar. A exclusão é garantida
 *   pelo TIPO (`RecentDocumentSources` não aceita `appointments`), e não por um
 *   filtro depois: filtro se apaga sem o `tsc` reclamar.
 * - **FICA DE FORA o lançamento financeiro.** É registro de caixa, não
 *   documento — e a RLS dele é owner/admin-only, então a lista encolheria para
 *   um `member` sem nada na tela explicando por quê (#38).
 */
import { buildClientTimeline, type ClientSummarySources, type ClientTimelineEntry } from '@/lib/clientSummary';

/**
 * As origens da lista da home.
 *
 * `Omit` das duas que a régua acima exclui: passar `appointments` aqui passa a
 * ser erro de compilação, e não uma linha a mais na tela que ninguém previu.
 */
export type RecentDocumentSources = Omit<ClientSummarySources, 'appointments' | 'financeEntries'>;

/** Quantos documentos a home mostra. */
export const RECENT_DOCUMENTS_LIMIT = 5;

/**
 * Os documentos mais recentes de qualquer tipo, do mais novo para o mais antigo.
 *
 * A ordenação, o descarte de linha sem data utilizável e a rota de cada tipo
 * vêm todos de `buildClientTimeline` — aqui só se escolhe QUEM entra e QUANTOS
 * aparecem.
 */
export function buildRecentDocuments(
  sources: RecentDocumentSources,
  limit: number = RECENT_DOCUMENTS_LIMIT,
): ClientTimelineEntry[] {
  const entries = buildClientTimeline({
    quotes: sources.quotes ?? [],
    serviceOrders: sources.serviceOrders ?? [],
    contracts: sources.contracts ?? [],
    receipts: sources.receipts ?? [],
    nfseDocuments: sources.nfseDocuments ?? [],
    // `appointments` e `financeEntries` OMITIDOS de propósito — ver o cabeçalho.
  });
  return limit > 0 ? entries.slice(0, limit) : entries;
}
